/**
 * Client-side PvP round fairness (commit–reveal) verification.
 *
 * Matches server rule (documented in docs/api-reference.md):
 * - `commit = SHA256(UTF8(fairness_preimage) ‖ salt_bytes)` (32 bytes)
 * - `client_commit_hash` = base64(SHA256(commit))
 * - `roulette_position` = first 8 bytes of `commit` as big-endian uint64 mod 100_000
 */

export type PvpFairnessVerifyFailureCode =
  | "missing_preimage"
  | "missing_salt"
  | "missing_commit_hash"
  | "invalid_salt_base64"
  | "invalid_commit_hash_base64"
  | "invalid_client_commit_hash_base64"
  | "commit_mismatch"
  | "mask_mismatch"
  | "position_mismatch"
  | "web_crypto_unavailable"

export type PvpFairnessVerifyResult =
  | { ok: true }
  | { ok: false; code: PvpFairnessVerifyFailureCode }

function timingSafeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false
  let x = 0
  for (let i = 0; i < a.length; i++) x |= a[i]! ^ b[i]!
  return x === 0
}

function base64ToBytes(b64: string): Uint8Array | null {
  const trimmed = b64.trim()
  if (!trimmed) return null
  try {
    const normalized = trimmed.replace(/-/g, "+").replace(/_/g, "/")
    const pad = normalized.length % 4
    const padded =
      pad === 0 ? normalized : normalized + "=".repeat(4 - pad)
    const bin = atob(padded)
    const out = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
    return out
  } catch {
    return null
  }
}

function concatUtf8AndBytes(utf8: Uint8Array, tail: Uint8Array): Uint8Array {
  const out = new Uint8Array(utf8.length + tail.length)
  out.set(utf8, 0)
  out.set(tail, utf8.length)
  return out
}

function normalizeFairnessNumeric(
  value: number | string | null | undefined
): number | null {
  if (value == null) return null
  if (typeof value === "number") {
    return Number.isFinite(value) ? Math.trunc(value) : null
  }
  if (typeof value === "string") {
    const t = value.trim()
    if (!t) return null
    const n = Number(t)
    return Number.isFinite(n) ? Math.trunc(n) : null
  }
  return null
}

async function sha256(data: Uint8Array): Promise<Uint8Array> {
  const input = data.slice()
  const buf = await crypto.subtle.digest("SHA-256", input)
  return new Uint8Array(buf)
}

export function deriveRoulettePositionFromCommit(commit32: Uint8Array): number {
  if (commit32.length < 8) return 0
  const dv = new DataView(
    commit32.buffer,
    commit32.byteOffset,
    Math.min(8, commit32.byteLength)
  )
  return Number(dv.getBigUint64(0, false) % 100_000n)
}

export interface PvpFairnessVerifyInput {
  fairness_preimage: string | null | undefined
  salt: string | null | undefined
  commit_hash: string | null | undefined
  client_commit_hash: string | null | undefined
  roulette_position?: number | string | null
}

export async function verifyPvpRoundFairness(
  input: PvpFairnessVerifyInput
): Promise<PvpFairnessVerifyResult> {
  if (typeof globalThis.crypto?.subtle?.digest !== "function") {
    return { ok: false, code: "web_crypto_unavailable" }
  }

  const preimage =
    typeof input.fairness_preimage === "string" ? input.fairness_preimage : ""
  if (!preimage.trim()) return { ok: false, code: "missing_preimage" }

  const saltStr = typeof input.salt === "string" ? input.salt : ""
  if (!saltStr.trim()) return { ok: false, code: "missing_salt" }

  const commitB64 =
    typeof input.commit_hash === "string" ? input.commit_hash : ""
  if (!commitB64.trim()) return { ok: false, code: "missing_commit_hash" }

  const saltBytes = base64ToBytes(saltStr)
  if (!saltBytes) return { ok: false, code: "invalid_salt_base64" }

  const expectedCommit = base64ToBytes(commitB64)
  if (!expectedCommit || expectedCommit.length !== 32) {
    return { ok: false, code: "invalid_commit_hash_base64" }
  }

  const preimageUtf8 = new TextEncoder().encode(preimage)
  const combined = concatUtf8AndBytes(preimageUtf8, saltBytes)

  let computedCommit: Uint8Array
  try {
    computedCommit = await sha256(combined)
  } catch {
    return { ok: false, code: "web_crypto_unavailable" }
  }

  if (!timingSafeEqual(computedCommit, expectedCommit)) {
    return { ok: false, code: "commit_mismatch" }
  }

  const clientB64 =
    typeof input.client_commit_hash === "string"
      ? input.client_commit_hash
      : ""
  if (clientB64.trim()) {
    const expectedMask = base64ToBytes(clientB64)
    if (!expectedMask || expectedMask.length !== 32) {
      return { ok: false, code: "invalid_client_commit_hash_base64" }
    }
    let computedMask: Uint8Array
    try {
      computedMask = await sha256(computedCommit)
    } catch {
      return { ok: false, code: "web_crypto_unavailable" }
    }
    if (!timingSafeEqual(computedMask, expectedMask)) {
      return { ok: false, code: "mask_mismatch" }
    }
  }

  const roulettePos = normalizeFairnessNumeric(input.roulette_position)
  if (roulettePos !== null) {
    const pos = deriveRoulettePositionFromCommit(computedCommit)
    if (pos !== roulettePos) {
      return { ok: false, code: "position_mismatch" }
    }
  }

  return { ok: true }
}
